import { observer } from 'mobx-react';
import { useStore } from 'app/context';
import { runInAction } from 'mobx';
import { Modal } from 'app/components/Modal';
import { useEffect, useMemo } from 'react';
import { useField, useFormikContext } from 'formik';
import { IJpgThemaDef, JpgTextDef, JpgZieleDef, mapFocusThemenToVM } from 'app/stores/ui/jpg.themen.ui.store';
import { IJpg25, JpgThema } from 'app/stores/bnr.document.store';
import { ITableColumn, TableModel } from '../../table/table.model';
import { Table } from '../../table/table';
import * as Yup from 'yup';
import { Button } from '../../common/Button';
import { DocumentVM } from 'app/stores/ui/document.ui.store';
import { UserModel } from 'app/models/core/user.model';

interface IDocumentSendModalProps {
	doc: DocumentVM;
}

export const steartsSchema = Yup.object().shape({
	VBL: Yup.boolean(),
	BSG: Yup.boolean(),
	LPV: Yup.boolean(),
	VAS: Yup.boolean(),
	KVS: Yup.boolean(),
	KME: Yup.boolean(),
	BBS: Yup.boolean(),
	UB: Yup.boolean(),
	LDP: Yup.boolean(),
	EDP: Yup.boolean(),
	EDT: Yup.boolean(),
});

export class Jpg25RoleVM {
	index: number = 0;
	def: IJpgThemaDef;
	shares: number[];
	[key: string]: any;

	constructor(def: IJpgThemaDef, shares: number[], index: number, users: UserModel[]) {
		this.index = index;
		this.def = def;
		this.shares = shares ? shares : [];

		users.forEach((u) => {
			this[u.bnrId.toString() as any] = this.shares.includes(u.bnrId);
		});
	}

	getState(bnrId: number) {
		return this[bnrId.toString() as any];
	}

	setState(bnrId: number, val: boolean) {
		this[bnrId.toString() as any] = val;
	}

	setAll(users: UserModel[], val: boolean) {
		users.forEach((u) => {
			this[u.bnrId.toString() as any] = val;
		});
	}

	getBnrIds(users: UserModel[]) {
		const res: number[] = [];
		users.forEach((u) => {
			if (this[u.bnrId.toString() as keyof Jpg25RoleVM]) {
				res.push(u.bnrId);
			}
		});
		return res;
	}
}

export const formatBool = (val: any) => {
	return <input type="checkbox" checked={val} disabled={true} />;
};

export const Jpg25DocumentShareModal = observer((props: IDocumentSendModalProps) => {
	const { uiStore } = useStore();

	const modalId = uiStore.modalIds.documentShareJpg25Modal;

	const { setFieldValue, setFieldTouched } = useFormikContext();
	const [field] = useField('meta'); // a bit ugly. but it works.
	const meta = field.value as IJpg25;
	const planYear = props.doc.planYear;
	const isModalVisible = uiStore.visibleModals.includes(modalId);

	const tm = useMemo(() => {
		const tm = new TableModel<Jpg25RoleVM, number>();

		// Early initialization even if prerequisites not met
		if (!props.doc.agt || !planYear) {
			tm.idType = 'number';
			tm.idProperty = 'index';
			return tm;
		}
		const cols: ITableColumn<Jpg25RoleVM, number>[] = [
			{
				label: 'Thema',
				path: 'def.thema',
				render: (row) => {
					// Row header - click to toggle all columns in this row
					const toggleRow = () => {
						const users = props.doc.agt!.users.filter((u) => u.bnrId !== props.doc.currentUser.bnrId);
						// Recalculate state at click time to avoid stale closures
						const currentlyAllChecked = users.every((u) => row.data.getState(u.bnrId));

						runInAction(() => {
							row.data.setAll(users, !currentlyAllChecked);
							tm.setRowData(tm.data);
						});
					};

					return (
						<div style={{ cursor: 'pointer' }} onClick={toggleRow}>
							{row.data.def.themaShort && <strong>{row.data.def.themaShort}:&nbsp;</strong>}
							{row.data.def.thema}
						</div>
					);
				},
			},
		];

		const currentUser = props.doc.currentUser;
		props.doc
			.agt!.users.filter((u) => u.bnrId !== currentUser.bnrId)
			.forEach((u) => {
				cols.push({
					label: u.name + ' ' + u.steartText,
					path: u.bnrId.toString(),
					format: formatBool,
					editType: 'checkbox',
					editable: true,
					// Header render - click to toggle all rows in this column
					headerRender: (tableTm) => {
						const bnrId = u.bnrId;
						const userName = u.name + ' ' + u.steartText;

						// Create an observer component to reactively track state
						const ColumnHeaderToggle = observer(() => {
							// Safety check: ensure we have data
							if (!tableTm.data || tableTm.data.length === 0) {
								return <span>{userName}</span>;
							}

							const toggleColumn = (e: React.MouseEvent) => {
								e.preventDefault();
								e.stopPropagation();

								// Recalculate the current state before toggling
								const currentlyAllChecked = tableTm.data.every((vm: Jpg25RoleVM) => vm.getState(bnrId));

								runInAction(() => {
									tableTm.data.forEach((vm: Jpg25RoleVM) => {
										vm.setState(bnrId, !currentlyAllChecked);
									});
									// Trigger re-render by calling setRowData
									tableTm.setRowData(tableTm.data);
								});
							};

							return (
								<span style={{ cursor: 'pointer' }} onClick={toggleColumn}>
									{userName}
								</span>
							);
						});

						return <ColumnHeaderToggle />;
					},
				});
			});

		tm.setCols(cols);
		tm.editMode = true;
		tm.hideEditModeHeader = true;
		tm.useDetachedFooter = true;
		tm.saveButtonText = 'Übernehmen';
		tm.idType = 'number';
		tm.idProperty = 'index';
		tm.editDisabled = false;
		tm.sortDisabled = true;
		return tm;
	}, [props.doc.agt, isModalVisible]);

	const onCancel = () => {
		runInAction(() => {
			uiStore.hideModal(modalId);
		});
	};

	useEffect(() => {
		if (!isModalVisible || !props.doc.agt || !planYear) return;

		const agt = props.doc.agt; // Capture in a const for TypeScript
		const focusThemen = meta.focusThemen ? meta.focusThemen : [];
		const all = mapFocusThemenToVM(focusThemen as JpgThema[], planYear);
		const data: Jpg25RoleVM[] = [];
		let i = 0;

		data.push(new Jpg25RoleVM(JpgTextDef, meta.textShare ? meta.textShare : [], i++, agt.users));
		data.push(new Jpg25RoleVM(JpgZieleDef, meta.zieleShare ? meta.zieleShare : [], i++, agt.users));

		all.forEach((t) => {
			data.push(new Jpg25RoleVM(t.def, t.focusThema ? t.focusThema.share : [], i++, agt.users));
		});
		tm.setRowData(data);
		tm.setEditMode(true);
	}, [meta.focusThemen, meta.textShare, meta.zieleShare, isModalVisible, tm, planYear, props.doc.agt]);

	const onSave = async () => {
		// Create a deep copy to avoid mutating the original Formik state
		const updatedMeta = JSON.parse(JSON.stringify(meta));

		tm.data.forEach((vm) => {
			if (vm.def.keyThema === 'text') {
				updatedMeta.textShare = vm.getBnrIds(props.doc.agt!.users);
				return;
			}
			if (vm.def.keyThema === 'ziele') {
				updatedMeta.zieleShare = vm.getBnrIds(props.doc.agt!.users);
				return;
			}
			const t = updatedMeta.focusThemen!.find((f: any) => f.key === vm.def.keyThema);
			if (t) {
				t.share = vm.getBnrIds(props.doc.agt!.users);
			}
		});
		// Set the field value and mark it as touched to trigger dirty state
		await setFieldValue('meta', updatedMeta, true);
		setFieldTouched('meta', true, false);
		uiStore.hideModal(modalId);
	};

	const allOrNone = () => {
		if (!props.doc.agt || !tm.data.length) return;
		const users = props.doc.agt.users.filter((u) => u.bnrId !== props.doc.currentUser.bnrId);
		if (users.length === 0) return;

		// Check if all are currently checked
		const allChecked = tm.data.every((vm) => users.every((u) => vm.getState(u.bnrId)));

		tm.data.forEach((vm) => {
			vm.setAll(users, !allChecked);
		});
		tm.setRowData(tm.data);
	};

	// Don't render modal content if prerequisites not met
	if (!props.doc.agt || !planYear) {
		return <></>;
	}

	return (
		<>
			<Modal modalId={modalId} title={'JPG teilen'} size="large" modifier="is-paddingless" onClose={onCancel}>
				<div className="pad-1rem">
					<Button type="button" className="button is-small is-primary is-inverted" onClick={allOrNone}>
						Alle / Keine
					</Button>
				</div>
				<div>
					<Table tm={tm} onSubmit={onSave} schema={steartsSchema} onCancel={onCancel} />
				</div>
			</Modal>
		</>
	);
});
